import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import {
  PlusIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  PauseIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../services/api';
import axios from 'axios';
import toast from 'react-hot-toast';

const MassTasks = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [jobs, setJobs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [filterNotUpdated, setFilterNotUpdated] = useState(false);
  const [restartFromStart, setRestartFromStart] = useState(false);
  const [serviceUrls, setServiceUrls] = useState({}); // Load service URLs for routing file uploads

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    taskType: 'update_dm_settings',
    accountIds: [],
    taskSettings: {
      allowDmsFrom: 'all',
      updateBannerImage: false,
      bannerImages: [],
      tweetsToDelete: 20
    },
    settings: {
      delayMin: 2,
      delayMax: 5
    }
  });

  const { socket } = useSocket();

  // Track which dropdowns are open
  const [openDropdowns, setOpenDropdowns] = useState(new Set());
  
  const handleToggleDropdown = useCallback((jobId) => {
    setOpenDropdowns(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  }, []);

  // Batch socket updates
  const updateQueueRef = useRef(new Map());
  const updateTimeoutRef = useRef(null);

  useEffect(() => {
    // Load service URLs for routing file uploads to automation service
    const loadServiceUrls = async () => {
      try {
        const response = await api.get('/config/services');
        setServiceUrls(response.data || {});
      } catch (error) {
        console.error('[MassTasks] Error loading service URLs:', error);
      }
    };
    
    loadServiceUrls();
    loadJobs();
    loadGroups();
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [formData.taskType]);

  useEffect(() => {
    if (!socket) return;

    socket.emit('join-room', 'mass-tasks-page');

    const flushUpdates = () => {
      if (updateQueueRef.current.size === 0) return;
      
      setJobs(prevJobs => {
        const updated = [...prevJobs];
        let changed = false;
        
        updateQueueRef.current.forEach((update, jobId) => {
          const index = updated.findIndex(j => j._id === jobId);
          if (index >= 0) {
            updated[index] = {
              ...updated[index],
              ...update,
              progress: {
                ...updated[index].progress,
                ...(update.progress || {})
              },
              ...(update.accountStatus && { accountStatus: update.accountStatus })
            };
            changed = true;
          }
        });
        
        updateQueueRef.current.clear();
        return changed ? updated : prevJobs;
      });
      
      updateTimeoutRef.current = null;
    };

    socket.on('mass-task-progress', (data) => {
      const existing = updateQueueRef.current.get(data.jobId) || {};
      updateQueueRef.current.set(data.jobId, {
        ...existing,
        progress: {
          ...existing.progress,
          ...data.progress
        },
        ...(data.accountStatus && { accountStatus: data.accountStatus }),
        ...(data.status && { status: data.status })
      });
      
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      updateTimeoutRef.current = setTimeout(flushUpdates, 300);
    });

    socket.on('mass-task-job-updated', (data) => {
      setJobs(prevJobs => {
        const index = prevJobs.findIndex(j => j._id === data.jobId || j._id?.toString() === data.jobId);
        if (index >= 0) {
          const updated = [...prevJobs];
          updated[index] = {
            ...updated[index],
            ...(data.status && { status: data.status }),
            ...(data.progress && { progress: { ...updated[index].progress, ...data.progress } }),
            ...(data.accountStatus && { accountStatus: data.accountStatus })
          };
          return updated;
        }
        // Job not found in list - reload to get latest data
        loadJobs();
        return prevJobs;
      });
    });

    return () => {
      socket.emit('leave-room', 'mass-tasks-page');
      socket.off('mass-task-progress');
      socket.off('mass-task-job-updated');
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [socket]);

  const loadJobs = async () => {
    try {
      const response = await api.get('/mass-task/jobs');
      setJobs(response.data || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
      setJobs([]);
      if (error.response?.status >= 500) {
        toast.error('Failed to load jobs');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const config = { timeout: 30000 };
      if (formData.taskType === 'status_check') {
        config.params = { taskType: 'status_check' };
      }
      const response = await api.get('/mass-task/accounts', config);
      setAccounts(response.data || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await api.get('/accounts/groups');
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const handleSelectAllAccounts = () => {
    const selectableAccounts = accounts.filter(acc => {
      if (acc.hasValidSession !== true || acc.inUse === true) return false;
      // Apply filter if enabled
      if (filterNotUpdated && acc.bioUpdated) return false;
      // Filter out accounts that already have DoB and phone for dob_phone_check task
      if (formData.taskType === 'dob_phone_check' && acc.hasDoBAndPhone) return false;
      // Filter out accounts without passwords for dob_phone_check task
      if (formData.taskType === 'dob_phone_check' && !acc.hasPassword) return false;
      // Filter out accounts already unchecked for privacy_check task
      if (formData.taskType === 'privacy_check' && acc.isUnprotected) return false;
      return true;
    });
    
    const allSelected = selectableAccounts.length > 0 && 
      selectableAccounts.every(acc => formData.accountIds.includes(acc._id));
    
    if (allSelected) {
      setFormData({
        ...formData,
        accountIds: formData.accountIds.filter(id => 
          !selectableAccounts.map(acc => acc._id).includes(id)
        )
      });
    } else {
      const newIds = [...new Set([
        ...formData.accountIds,
        ...selectableAccounts.map(acc => acc._id)
      ])];
      setFormData({ ...formData, accountIds: newIds });
    }
  };

  const handleToggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const handleSelectGroup = (groupName) => {
    const groupAccounts = accounts.filter(acc => {
      if (acc.group !== groupName) return false;
      if (acc.hasValidSession !== true || acc.inUse === true) return false;
      // Apply filter if enabled
      if (filterNotUpdated && formData.taskType === 'update_bio' && acc.bioUpdated) return false;
      // Filter out accounts that already have DoB and phone for dob_phone_check task
      if (formData.taskType === 'dob_phone_check' && acc.hasDoBAndPhone) return false;
      // Filter out accounts without passwords for dob_phone_check task
      if (formData.taskType === 'dob_phone_check' && !acc.hasPassword) return false;
      // Filter out accounts already unchecked for privacy_check task
      if (formData.taskType === 'privacy_check' && acc.isUnprotected) return false;
      return true;
    });
    
    if (groupAccounts.length === 0) return;
    
    const groupAccountIds = groupAccounts.map(acc => acc._id);
    const allGroupSelected = groupAccountIds.every(id => formData.accountIds.includes(id));
    
    if (allGroupSelected) {
      // Deselect all accounts in group
      setFormData({
        ...formData,
        accountIds: formData.accountIds.filter(id => !groupAccountIds.includes(id))
      });
    } else {
      // Select all accounts in group
      setFormData({
        ...formData,
        accountIds: [...new Set([...formData.accountIds, ...groupAccountIds])]
      });
    }
  };

  const handleCreateJob = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('Job name is required');
        return;
      }

      if (formData.accountIds.length === 0) {
        toast.error('Please select at least one account');
        return;
      }

      // Validate bio update requirements
      if (formData.taskType === 'update_bio') {
        if (!formData.taskSettings.updateName && !formData.taskSettings.updateDescription && !formData.taskSettings.updateProfilePicture && !formData.taskSettings.updateBannerImage) {
          toast.error('Please select at least one option to update (Name, Description, Profile Picture, or Banner Image)');
          return;
        }
        if (formData.taskSettings.updateName && (!formData.taskSettings.names || formData.taskSettings.names.length === 0)) {
          toast.error('Please provide at least one display name');
          return;
        }
        if (formData.taskSettings.updateDescription && (!formData.taskSettings.descriptions || formData.taskSettings.descriptions.length === 0)) {
          toast.error('Please provide at least one bio');
          return;
        }
        if (formData.taskSettings.updateProfilePicture && (!formData.taskSettings.profilePictures || formData.taskSettings.profilePictures.length === 0)) {
          toast.error('Please select at least one profile picture');
          return;
        }
        if (formData.taskSettings.updateBannerImage && (!formData.taskSettings.bannerImages || formData.taskSettings.bannerImages.length === 0)) {
          toast.error('Please select at least one banner image');
          return;
        }
      }

      if (formData.taskType === 'delete_tweets') {
        const n = formData.taskSettings.tweetsToDelete ?? 20;
        if (typeof n !== 'number' || n < 1 || n > 200) {
          toast.error('Tweets to delete per account must be between 1 and 200');
          return;
        }
      }

      // Use FormData for file uploads, otherwise use JSON
      const hasProfilePictures = formData.taskType === 'update_bio' && formData.taskSettings.updateProfilePicture && formData.taskSettings.profilePictures && formData.taskSettings.profilePictures.length > 0;
      const hasBannerImages = formData.taskType === 'update_bio' && formData.taskSettings.updateBannerImage && formData.taskSettings.bannerImages && formData.taskSettings.bannerImages.length > 0;
      
      if (hasProfilePictures || hasBannerImages) {
        // For file uploads, POST directly to automation service (bypasses redirect that loses auth token)
        const serviceUrl = serviceUrls.automation;
        if (!serviceUrl) {
          toast.error('Automation service not available. Cannot upload files.');
          return;
        }
        
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name);
        formDataToSend.append('taskType', formData.taskType);
        formDataToSend.append('accountIds', JSON.stringify(formData.accountIds));
        formDataToSend.append('taskSettings', JSON.stringify({
          ...formData.taskSettings,
          profilePictures: [], // Don't send file objects in JSON, they'll be in FormData
          bannerImages: [] // Don't send file objects in JSON, they'll be in FormData
        }));
        formDataToSend.append('settings', JSON.stringify(formData.settings));
        
        // Append profile pictures
        if (hasProfilePictures) {
          formData.taskSettings.profilePictures.forEach((file) => {
            if (file instanceof File) {
              formDataToSend.append('profilePictures', file);
            }
          });
        }
        
        // Append banner images
        if (hasBannerImages) {
          formData.taskSettings.bannerImages.forEach((file) => {
            if (file instanceof File) {
              formDataToSend.append('bannerImages', file);
            }
          });
        }

        // Use axios directly to POST to automation service with Authorization header
        // This bypasses the redirect that would lose the auth token
        const requestUrl = `${serviceUrl}/api/mass-task/jobs`;
        
        await axios.post(requestUrl, formDataToSend, {
          timeout: 60000, // 60 second timeout for file uploads
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
            // Don't set Content-Type - let browser set it with boundary for FormData
          }
        });
      } else {
        const payload = {
          name: formData.name,
          taskType: formData.taskType,
          accountIds: formData.accountIds,
          taskSettings: {
            ...formData.taskSettings,
            profilePictures: [], // Clear file objects, they're not needed for non-file uploads
            bannerImages: [] // Clear file objects, they're not needed for non-file uploads
          },
          settings: formData.settings
        };

        await api.post('/mass-task/jobs', payload);
      }

      toast.success('Job created successfully');
      setShowCreateModal(false);
      resetForm();
      loadJobs();
    } catch (error) {
      console.error('Error creating job:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 403) {
        setShowPermissionModal(true);
      } else {
        const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Failed to create job';
        toast.error(errorMessage);
      }
    }
  };

  const handleEditJob = (job) => {
    setFormData({
      name: job.name,
      taskType: job.taskType,
      accountIds: job.accountIds.map(acc => acc._id || acc),
      taskSettings: job.taskSettings || { allowDmsFrom: 'all' },
      settings: job.settings || { delayMin: 2, delayMax: 5 }
    });
    setEditingJob(job);
    setShowEditModal(true);
  };

  const handleUpdateJob = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('Job name is required');
        return;
      }

      if (formData.accountIds.length === 0) {
        toast.error('Please select at least one account');
        return;
      }

      const payload = {
        name: formData.name,
        accountIds: formData.accountIds,
        taskSettings: formData.taskSettings,
        settings: formData.settings
      };

      const wasPaused = editingJob?.status === 'paused';
      
      await api.put(`/mass-task/jobs/${editingJob._id}`, payload);
      toast.success('Job updated successfully');
      
      // If restart from start is checked, reset progress and restart
      if (restartFromStart) {
        try {
          await api.post(`/mass-task/jobs/${editingJob._id}/restart-from-start`);
          await api.post(`/mass-task/jobs/${editingJob._id}/start`);
          toast.success('Job reset and restarted from the beginning');
        } catch (error) {
          console.error('Error restarting from start:', error);
          toast.error('Job updated but failed to restart from start. Please start it manually.');
        }
      } else if (wasPaused) {
        // If job was paused, resume it automatically
        try {
          await api.post(`/mass-task/jobs/${editingJob._id}/start`);
          toast.success('Job resumed from where it was paused');
        } catch (error) {
          console.error('Error resuming job:', error);
          toast.error('Job updated but failed to resume. Please start it manually.');
        }
      }
      
      setShowEditModal(false);
      setEditingJob(null);
      setRestartFromStart(false);
      resetForm();
      loadJobs();
    } catch (error) {
      console.error('Error updating job:', error);
      toast.error(error.response?.data?.error || 'Failed to update job');
    }
  };

  const handleStartJob = async (jobId) => {
    setJobs(prevJobs => {
      const index = prevJobs.findIndex(j => j._id === jobId);
      if (index >= 0) {
        const updated = [...prevJobs];
        updated[index] = { ...updated[index], status: 'running' };
        return updated;
      }
      return prevJobs;
    });

    try {
      await api.post(`/mass-task/jobs/${jobId}/start`);
      toast.success('Job started');
    } catch (error) {
      console.error('Error starting job:', error);
      toast.error(error.response?.data?.error || 'Failed to start job');
      loadJobs();
    }
  };

  const handlePauseJob = async (jobId) => {
    setJobs(prevJobs => {
      const index = prevJobs.findIndex(j => j._id === jobId);
      if (index >= 0) {
        const updated = [...prevJobs];
        updated[index] = { ...updated[index], status: 'paused' };
        return updated;
      }
      return prevJobs;
    });

    try {
      await api.post(`/mass-task/jobs/${jobId}/stop`);
      toast.success('Job paused');
    } catch (error) {
      console.error('Error pausing job:', error);
      toast.error(error.response?.data?.error || 'Failed to pause job');
      loadJobs();
    }
  };

  const handleStopJob = async (jobId) => {
    setJobs(prevJobs => {
      const index = prevJobs.findIndex(j => j._id === jobId);
      if (index >= 0) {
        const updated = [...prevJobs];
        updated[index] = { ...updated[index], status: 'stopped' };
        return updated;
      }
      return prevJobs;
    });

    try {
      await api.post(`/mass-task/jobs/${jobId}/stop`);
      toast.success('Job stopped');
      loadJobs();
    } catch (error) {
      console.error('Error stopping job:', error);
      toast.error(error.response?.data?.error || 'Failed to stop job');
      loadJobs();
    }
  };

  const handleRetryFailed = async (jobId) => {
    try {
      const result = await api.post(`/mass-task/jobs/${jobId}/retry-failed`);
      toast.success(result.data?.message || 'Retrying failed accounts...');
      loadJobs();
    } catch (error) {
      console.error('Error retrying failed accounts:', error);
      toast.error(error.response?.data?.error || 'Failed to retry failed accounts');
      loadJobs();
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      await api.delete(`/mass-task/jobs/${jobId}`);
      toast.success('Job deleted');
      loadJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error(error.response?.data?.error || 'Failed to delete job');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      taskType: 'update_dm_settings',
      accountIds: [],
      taskSettings: {
        allowDmsFrom: 'all',
        updateBannerImage: false,
        bannerImages: [],
        tweetsToDelete: 20
      },
      settings: {
        delayMin: 2,
        delayMax: 5
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'stopped':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'pending':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getProgressPercentage = (job) => {
    if (!job.progress || job.progress.total === 0) return 0;
    const completed = (job.progress.completed || 0) + (job.progress.failed || 0) + (job.progress.skipped || 0);
    return Math.round((completed / job.progress.total) * 100);
  };

  const getTaskTypeLabel = (taskType) => {
    switch (taskType) {
      case 'update_dm_settings':
        return 'Update DM Settings';
      case 'update_bio':
        return 'Bio Update';
      case 'dob_phone_check':
        return 'DoB Phone check';
      case 'extract_profile':
        return 'Extract Profile';
      case 'privacy_check':
        return 'Privacy check';
      case 'fetch_username':
        return 'Fetch Username';
      case 'delete_tweets':
        return 'Delete tweets';
      case 'account_stats_browser':
        return 'Account stats (browser / Patchright)';
      case 'status_check':
        return 'Status check';
      default:
        return taskType;
    }
  };

  const getTaskTypeIcon = (taskType) => {
    switch (taskType) {
      case 'update_dm_settings':
        return <EnvelopeIcon className="h-4 w-4" />;
      case 'update_bio':
        return <UserGroupIcon className="h-4 w-4" />;
      case 'privacy_check':
        return <ShieldCheckIcon className="h-4 w-4" />;
      case 'status_check':
        return <ArrowPathIcon className="h-4 w-4" />;
      case 'delete_tweets':
        return <TrashIcon className="h-4 w-4" />;
      case 'account_stats_browser':
        return <ChartBarIcon className="h-4 w-4" />;
      default:
        return <Cog6ToothIcon className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className={`relative overflow-hidden rounded-2xl ${
        isDarkMode 
          ? 'bg-gradient-to-br from-indigo-950 via-violet-950 to-slate-900' 
          : 'bg-gradient-to-br from-indigo-50 via-violet-100 to-indigo-100'
      }`}>
        <div className="absolute inset-0 overflow-hidden">
          <div className={`absolute -top-16 left-1/3 w-72 h-72 rounded-full blur-3xl ${
            isDarkMode ? 'bg-indigo-600/20' : 'bg-indigo-300/40'
          }`} />
          <div className={`absolute -bottom-20 -right-16 w-80 h-80 rounded-full blur-3xl ${
            isDarkMode ? 'bg-violet-600/15' : 'bg-violet-300/30'
          }`} />
        </div>
        
        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className={`relative flex items-center justify-center w-16 h-16 rounded-2xl ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/30' 
                  : 'bg-gradient-to-br from-indigo-500 to-violet-500 shadow-lg shadow-indigo-400/40'
              }`}>
                <Cog6ToothIcon className="w-8 h-8 text-white" />
              </div>
              
              <div>
                <h1 className={`text-3xl font-bold tracking-tight ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Mass Tasks
                </h1>
                <p className={`mt-1.5 text-base ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  Bulk operations for account settings
                </p>
              </div>
            </div>
            
            <button
              onClick={() => {
                if (user?.role !== 'admin' && !user?.features?.canUseMassTasks) {
                  setShowPermissionModal(true);
                  return;
                }
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors shadow-lg shadow-indigo-600/30"
            >
              <PlusIcon className="w-5 h-5" />
              Create Task
            </button>
          </div>
        </div>
      </div>

      {/* Jobs List */}
      <div className="space-y-4">
        {jobs.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-12">
              <Cog6ToothIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No tasks yet</h3>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Create your first mass task to update account settings in bulk.
              </p>
            </div>
          </div>
        ) : (
          jobs.map((job) => (
            <JobCard
              key={job._id}
              job={job}
              onStart={handleStartJob}
              onStop={handleStopJob}
              onPause={handlePauseJob}
              onRetryFailed={handleRetryFailed}
              onEdit={handleEditJob}
              onDelete={handleDeleteJob}
              getStatusColor={getStatusColor}
              getProgressPercentage={getProgressPercentage}
              getTaskTypeLabel={getTaskTypeLabel}
              getTaskTypeIcon={getTaskTypeIcon}
              isDropdownOpen={openDropdowns.has(job._id.toString())}
              onToggleDropdown={() => handleToggleDropdown(job._id.toString())}
            />
          ))
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <JobModal
          title="Create Mass Task"
          formData={formData}
          setFormData={setFormData}
          accounts={accounts}
          groups={groups}
          expandedGroups={expandedGroups}
          onToggleGroup={handleToggleGroup}
          onSelectGroup={handleSelectGroup}
          onSubmit={handleCreateJob}
          onClose={() => {
            setShowCreateModal(false);
            setFilterNotUpdated(false);
            resetForm();
          }}
          submitLabel="Create Task"
          handleSelectAllAccounts={handleSelectAllAccounts}
          filterNotUpdated={filterNotUpdated}
          setFilterNotUpdated={setFilterNotUpdated}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && editingJob && (
        <JobModal
          title="Edit Mass Task"
          formData={formData}
          setFormData={setFormData}
          accounts={accounts}
          onSubmit={handleUpdateJob}
          onClose={() => {
            setShowEditModal(false);
            setEditingJob(null);
            setRestartFromStart(false);
            resetForm();
          }}
          submitLabel="Update Task"
          handleSelectAllAccounts={handleSelectAllAccounts}
          groups={groups}
          expandedGroups={expandedGroups}
          onToggleGroup={handleToggleGroup}
          onSelectGroup={handleSelectGroup}
          restartFromStart={restartFromStart}
          setRestartFromStart={setRestartFromStart}
          isEditing
        />
      )}

      {/* Permission Modal */}
      {showPermissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Feature Not Available
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You don't have permission to use Mass Tasks. Please contact support to enable this feature.
            </p>
            <button
              onClick={() => setShowPermissionModal(false)}
              className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Job Card Component (memoized for performance)
const JobCard = memo(({ job, onStart, onStop, onPause, onRetryFailed, onEdit, onDelete, getStatusColor, getProgressPercentage, getTaskTypeLabel, getTaskTypeIcon, isDropdownOpen, onToggleDropdown }) => {
  const [showDetails, setShowDetails] = useState(isDropdownOpen);

  useEffect(() => {
    setShowDetails(isDropdownOpen);
  }, [isDropdownOpen]);

  // Helper function to format error messages into short, readable text
  const formatError = (error) => {
    if (!error) return '';
    
    // Common error patterns
    if (error.includes('profile_picture_failed') || error.includes('Profile picture update failed')) {
      return 'Profile picture failed';
    }
    if (error.includes('session_expired') || error.includes('Session expired')) {
      return 'Session expired';
    }
    if (error.includes('rate_limit') || error.includes('Rate limit')) {
      return 'Rate limited';
    }
    if (error.includes('network_error') || error.includes('Network error')) {
      return 'Network error';
    }
    if (error.includes('api_error') || error.includes('API error')) {
      return 'API error';
    }
    
    // Truncate long errors to 50 characters
    if (error.length > 50) {
      return error.substring(0, 47) + '...';
    }
    
    return error;
  };

  const getStatusBadgeColor = (status) => {
    if (status === 'pending') return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    if (status === 'processing') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
    if (status === 'completed') return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    if (status === 'active') return 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-300';
    if (status === 'suspended') return 'bg-orange-100 text-orange-900 dark:bg-orange-900/30 dark:text-orange-300';
    if (status === 'captcha') return 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-300';
    if (status === 'auth_expired') return 'bg-violet-100 text-violet-900 dark:bg-violet-900/30 dark:text-violet-300';
    if (status === 'unavailable') return 'bg-slate-200 text-slate-800 dark:bg-slate-600/40 dark:text-slate-200';
    if (status === 'failed') return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    if (status === 'skipped') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  const progress = job.progress || { total: 0, completed: 0, failed: 0, skipped: 0, suspended: 0 };
  const pending = progress.total - (progress.completed + progress.failed + progress.skipped);

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {job.name}
              </h3>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(job.status)}`}>
                {job.status}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <UserGroupIcon className="h-4 w-4" />
                {job.accountIds?.length || 0} account{(job.accountIds?.length || 0) !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                {getTaskTypeIcon(job.taskType)}
                {getTaskTypeLabel(job.taskType)}
              </span>
              {job.taskType === 'update_dm_settings' && job.taskSettings?.allowDmsFrom && (
                <span className="text-xs text-gray-500">
                  → {job.taskSettings.allowDmsFrom}
                </span>
              )}
              {job.taskType === 'delete_tweets' && job.taskSettings?.tweetsToDelete != null && (
                <span className="text-xs text-gray-500">
                  → {job.taskSettings.tweetsToDelete} per account
                </span>
              )}
            </div>
            {job.status === 'running' && job.accountStatus && job.accountStatus.length > 0 && (
              <div className="mt-2 flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400 flex-wrap">
                <span className="flex items-center gap-1">
                  <UserGroupIcon className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                  {job.accountStatus.length} total
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircleIcon className="h-3 w-3 text-green-600" />
                  {job.accountStatus.filter(acc => acc.status === 'completed' || acc.status === 'active').length} done
                </span>
                {job.accountStatus.filter(acc => acc.status === 'processing').length > 0 && (
                  <span className="flex items-center gap-1">
                    <ClockIcon className="h-3 w-3 text-blue-600" />
                    {job.accountStatus.filter(acc => acc.status === 'processing').length} processing
                  </span>
                )}
                {job.accountStatus.filter(acc => acc.status === 'failed').length > 0 && (
                  <span className="flex items-center gap-1">
                    <XCircleIcon className="h-3 w-3 text-red-600" />
                    {job.accountStatus.filter(acc => acc.status === 'failed').length} failed
                  </span>
                )}
                {job.taskType === 'status_check' && job.accountStatus.filter(acc => acc.status === 'suspended').length > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-orange-500" />
                    {job.accountStatus.filter(acc => acc.status === 'suspended').length} suspended
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {job.status === 'running' ? (
              <>
                <button
                  onClick={() => onPause(job._id)}
                  className="p-2 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-xl transition-colors"
                  title="Pause task"
                >
                  <PauseIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => onStop(job._id)}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                  title="Stop task"
                >
                  <StopIcon className="h-5 w-5" />
                </button>
              </>
            ) : job.status === 'paused' || job.status === 'failed' ? (
              <>
                <button
                  onClick={() => onStart(job._id)}
                  className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-xl transition-colors"
                  title={job.status === 'failed' ? 'Retry task' : 'Resume task'}
                >
                  <PlayIcon className="h-5 w-5" />
                </button>
                {job.status !== 'failed' && (
                  <button
                    onClick={() => onStop(job._id)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                    title="Stop task"
                  >
                    <StopIcon className="h-5 w-5" />
                  </button>
                )}
              </>
            ) : job.status === 'pending' || job.status === 'stopped' ? (
              <button
                onClick={() => onStart(job._id)}
                className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors"
                title="Start task"
              >
                <PlayIcon className="h-5 w-5" />
              </button>
            ) : job.status === 'completed' && pending > 0 ? (
              <button
                onClick={() => onStart(job._id)}
                className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors"
                title="Restart task (continue with pending)"
              >
                <PlayIcon className="h-5 w-5" />
              </button>
            ) : job.status === 'completed' && job.accountStatus && job.accountStatus.filter(acc => acc.status === 'failed').length > 0 && 
                (job.taskType === 'update_dm_settings' || job.taskType === 'update_bio') ? (
              <button
                onClick={() => onRetryFailed(job._id)}
                className="p-2 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-md transition-colors"
                title={`Retry ${job.accountStatus.filter(acc => acc.status === 'failed').length} failed account(s)`}
              >
                <ArrowPathIcon className="h-5 w-5" />
              </button>
            ) : null}
            {(job.status === 'pending' || job.status === 'stopped' || job.status === 'paused' || job.status === 'failed' || job.status === 'completed') && (
              <button
                onClick={() => onEdit(job)}
                className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-colors"
                title="Edit task"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={() => onDelete(job._id)}
              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
              title="Delete task"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        {(job.status === 'running' || job.status === 'completed' || job.status === 'paused') && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Progress: {progress.completed + progress.failed + progress.skipped} / {progress.total}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {getProgressPercentage(job)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-indigo-600 transition-all duration-300"
                style={{ width: `${getProgressPercentage(job)}%` }}
              />
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <CheckCircleIcon className="h-3 w-3 text-green-600" />
                {progress.completed} sent
              </span>
              <span className="flex items-center gap-1">
                <XCircleIcon className="h-3 w-3 text-red-600" />
                {progress.failed} failed
              </span>
              {job.taskType === 'status_check' && (
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-orange-500" />
                  {progress.suspended ?? 0} suspended
                </span>
              )}
              <span className="flex items-center gap-1">
                <ClockIcon className="h-3 w-3 text-yellow-600" />
                {progress.skipped} skipped
              </span>
              <span className="flex items-center gap-1">
                <ClockIcon className="h-3 w-3 text-gray-400" />
                {pending > 0 ? pending : 0} pending
              </span>
            </div>
          </div>
        )}

        {/* Account Details Section */}
        {job.accountStatus && job.accountStatus.length > 0 && (
          <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
            <button
              onClick={onToggleDropdown}
              className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <span className="flex items-center gap-2">
                <span>Account Details</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({job.accountStatus.length} accounts)
                </span>
              </span>
              {showDetails ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )}
            </button>
            
            {showDetails && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {job.accountStatus.map((accStatus) => (
                  <div
                    key={accStatus.accountId?._id || accStatus.accountId}
                    className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-2xl text-sm"
                  >
                    <div className="font-medium text-gray-900 dark:text-white truncate">
                      @{accStatus.accountId?.username || 'Unknown'}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadgeColor(accStatus.status)}`}>
                        {accStatus.status}
                      </span>
                    </div>
                    {accStatus.error && (
                      <div className="text-xs text-red-500 dark:text-red-400 mt-1 truncate" title={accStatus.error}>
                        {formatError(accStatus.error)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

// Job Modal Component (shared between Create and Edit)
const JobModal = ({ title, formData, setFormData, accounts, groups = [], expandedGroups = new Set(), onToggleGroup, onSelectGroup, onSubmit, onClose, submitLabel, handleSelectAllAccounts, isEditing = false, restartFromStart = false, setRestartFromStart = null, filterNotUpdated = false, setFilterNotUpdated = null }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Job Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Task Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter task name"
            />
          </div>

          {/* Task Type */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Task Type *
              </label>
              <select
                value={formData.taskType}
                onChange={(e) => {
                  const newTaskType = e.target.value;
                  // Reset filter when task type changes
                  if (newTaskType !== 'update_bio' && setFilterNotUpdated) {
                    setFilterNotUpdated(false);
                  }
                  setFormData({ 
                    ...formData, 
                    taskType: newTaskType,
                    taskSettings: newTaskType === 'update_bio' ? {
                      updateName: false,
                      updateDescription: false,
                      updateProfilePicture: false,
                      updateBannerImage: false,
                      names: [],
                      descriptions: [],
                      profilePictures: [],
                      bannerImages: []
                    } : newTaskType === 'delete_tweets' ? { tweetsToDelete: formData.taskSettings?.tweetsToDelete ?? 20 } : formData.taskSettings
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="update_dm_settings">Update DM Settings</option>
                <option value="update_bio">Bio Update</option>
                <option value="dob_phone_check">DoB Phone check</option>
                <option value="extract_profile">Extract Profile</option>
                <option value="privacy_check">Privacy check</option>
                <option value="fetch_username">Fetch Username</option>
                <option value="delete_tweets">Delete tweets</option>
                <option value="account_stats_browser">Account stats (browser / Patchright)</option>
                <option value="status_check">Status check</option>
              </select>
              {formData.taskType === 'status_check' && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Calls UserByScreenName for each account&apos;s username. Sets global status to captcha, suspended, or auth expired when X reports it; on success sets status to logged-in (active).
                </p>
              )}
              {formData.taskType === 'account_stats_browser' && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Opens each account&apos;s own profile in headed Chrome (same persistent profile as V3 follow), reads follower/following labels, and flags captchas from network + page text. Requires{' '}
                  <code className="text-xs">LOGIN_WORKER_URL</code> and Patchright on the worker.
                </p>
              )}
            </div>
          )}

          {/* Task Settings - DM Settings */}
          {formData.taskType === 'update_dm_settings' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Allow DMs From *
              </label>
              <select
                value={formData.taskSettings.allowDmsFrom}
                onChange={(e) => setFormData({
                  ...formData,
                  taskSettings: { ...formData.taskSettings, allowDmsFrom: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">Everyone</option>
                <option value="following">People you follow</option>
                <option value="verified">Verified users only</option>
                <option value="none">No one</option>
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This will update the "Allow direct messages from" setting for all selected accounts.
              </p>
            </div>
          )}

          {/* Task Settings - DoB Phone Check */}
          {formData.taskType === 'dob_phone_check' && (
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.taskSettings.addDateOfBirth || false}
                  onChange={(e) => setFormData({
                    ...formData,
                    taskSettings: { ...formData.taskSettings, addDateOfBirth: e.target.checked }
                  })}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Add date of birth</span>
              </label>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                If no date of birth is found, a random date of birth will be added to the account.
              </p>
            </div>
          )}

          {/* Task Settings - Delete tweets */}
          {formData.taskType === 'delete_tweets' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tweets to delete per account *
              </label>
              <input
                type="number"
                min={1}
                max={200}
                value={formData.taskSettings.tweetsToDelete ?? 20}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 200) {
                    setFormData({
                      ...formData,
                      taskSettings: { ...formData.taskSettings, tweetsToDelete: v }
                    });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Number of most recent tweets to delete per account (1–200). Fetches timeline then deletes from oldest in batch to newest.
              </p>
            </div>
          )}

          {/* Task Settings - Bio Update */}
          {formData.taskType === 'update_bio' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  What to Update
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.taskSettings.updateName || false}
                      onChange={(e) => setFormData({
                        ...formData,
                        taskSettings: { ...formData.taskSettings, updateName: e.target.checked }
                      })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Update Display Name</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.taskSettings.updateDescription || false}
                      onChange={(e) => setFormData({
                        ...formData,
                        taskSettings: { ...formData.taskSettings, updateDescription: e.target.checked }
                      })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Update Bio (Description)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.taskSettings.updateProfilePicture || false}
                      onChange={(e) => setFormData({
                        ...formData,
                        taskSettings: { ...formData.taskSettings, updateProfilePicture: e.target.checked }
                      })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Update Profile Picture</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.taskSettings.updateBannerImage || false}
                      onChange={(e) => setFormData({
                        ...formData,
                        taskSettings: { ...formData.taskSettings, updateBannerImage: e.target.checked }
                      })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Update Banner Image</span>
                  </label>
                </div>
              </div>

              {/* Names Input */}
              {formData.taskSettings.updateName && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Names (one per line) *
                  </label>
                  <textarea
                    value={Array.isArray(formData.taskSettings.names) 
                      ? formData.taskSettings.names.join('\n') 
                      : (formData.taskSettings.names || '')}
                    onChange={(e) => setFormData({
                      ...formData,
                      taskSettings: { 
                        ...formData.taskSettings, 
                        names: e.target.value.split('\n').map(n => n.trim()).filter(n => n.length > 0)
                      }
                    })}
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Name 1&#10;Name 2&#10;Name 3"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Enter one name per line. Names will be rotated across accounts sequentially.
                  </p>
                </div>
              )}

              {/* Descriptions Input */}
              {formData.taskSettings.updateDescription && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bios (one per line) *
                  </label>
                  <textarea
                    value={Array.isArray(formData.taskSettings.descriptions) 
                      ? formData.taskSettings.descriptions.join('\n') 
                      : (formData.taskSettings.descriptions || '')}
                    onChange={(e) => setFormData({
                      ...formData,
                      taskSettings: { 
                        ...formData.taskSettings, 
                        descriptions: e.target.value.split('\n').map(d => d.trim()).filter(d => d.length > 0)
                      }
                    })}
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Bio 1&#10;Bio 2&#10;Bio 3"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Enter one bio per line. Bios will be rotated across accounts sequentially.
                  </p>
                </div>
              )}

              {/* Profile Pictures Upload */}
              {formData.taskSettings.updateProfilePicture && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Profile Pictures *
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      setFormData({
                        ...formData,
                        taskSettings: {
                          ...formData.taskSettings,
                          profilePictures: files
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Select multiple images. Images will be rotated across accounts sequentially. If you have 50 accounts and 10 images, each image will be used 5 times.
                  </p>
                  {formData.taskSettings.profilePictures && formData.taskSettings.profilePictures.length > 0 && (
                    <p className="mt-1 text-xs text-indigo-600 dark:text-indigo-400">
                      {formData.taskSettings.profilePictures.length} image(s) selected
                    </p>
                  )}
                </div>
              )}

              {/* Banner Images Upload */}
              {formData.taskSettings.updateBannerImage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Banner Images *
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      setFormData({
                        ...formData,
                        taskSettings: {
                          ...formData.taskSettings,
                          bannerImages: files
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Select multiple images. Images will be rotated across accounts sequentially. If you have 50 accounts and 10 images, each image will be used 5 times.
                  </p>
                  {formData.taskSettings.bannerImages && formData.taskSettings.bannerImages.length > 0 && (
                    <p className="mt-1 text-xs text-indigo-600 dark:text-indigo-400">
                      {formData.taskSettings.bannerImages.length} image(s) selected
                    </p>
                  )}
                </div>
              )}

            </div>
          )}

          {/* Accounts Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Select Accounts *
              </label>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {formData.accountIds.length} of {accounts.filter(acc => {
                  if (acc.hasValidSession !== true || acc.inUse === true) return false;
                  if (filterNotUpdated && formData.taskType === 'update_bio' && acc.bioUpdated) return false;
                  if (formData.taskType === 'dob_phone_check' && acc.hasDoBAndPhone) return false;
                  if (formData.taskType === 'dob_phone_check' && !acc.hasPassword) return false;
                  if (formData.taskType === 'privacy_check' && acc.isUnprotected) return false;
                  return true;
                }).length} selected
              </span>
            </div>
            {/* Filter checkbox - show for update_bio tasks */}
            {formData.taskType === 'update_bio' && setFilterNotUpdated && (
              <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filterNotUpdated || false}
                    onChange={(e) => setFilterNotUpdated(e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Show only accounts not updated yet
                  </span>
                </label>
                <p className="mt-1 ml-6 text-xs text-gray-500 dark:text-gray-400">
                  Filter to show only accounts that haven't had their bio/profile picture updated yet
                </p>
              </div>
            )}
            <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-3">
              {accounts.length > 0 && (
                <label className="flex items-center gap-2 cursor-pointer pb-2 border-b border-gray-200 dark:border-gray-700 mb-2">
                  <input
                    type="checkbox"
                    checked={(() => {
                      const selectableAccounts = accounts.filter(acc => {
                        if (acc.hasValidSession !== true || acc.inUse === true) return false;
                        if (filterNotUpdated && formData.taskType === 'update_bio' && acc.bioUpdated) return false;
                        // For dob_phone_check: exclude accounts without passwords or that already have DoB+Phone
                        if (formData.taskType === 'dob_phone_check' && acc.hasDoBAndPhone) return false;
                        if (formData.taskType === 'dob_phone_check' && !acc.hasPassword) return false;
                        // For privacy_check: exclude accounts already unchecked
                        if (formData.taskType === 'privacy_check' && acc.isUnprotected) return false;
                        return true;
                      });
                      return selectableAccounts.length > 0 && 
                             selectableAccounts.every(acc => formData.accountIds.includes(acc._id));
                    })()}
                    onChange={handleSelectAllAccounts}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select All Available
                  </span>
                </label>
              )}
              {accounts.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 p-2">
                  No accounts found. Please add accounts first.
                </p>
              ) : (
                <>
                  {/* Groups Section */}
                  {(() => {
                    // Get unique groups from accounts (only show groups that have accounts in the current list)
                    // Normalize group names (trim and handle null/undefined)
                    const accountGroups = [...new Set(accounts
                      .map(acc => acc.group)
                      .filter(g => g != null && String(g).trim() !== '')
                      .map(g => String(g).trim()))];
                    
                    // Only show groups that have at least one account in the current accounts array
                    // This ensures we don't show empty groups
                    if (accountGroups.length === 0) return null;
                    
                    return (
                      <div className="space-y-1 mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                          Groups
                        </div>
                        {accountGroups.sort().map((groupName) => {
                          // Match accounts by normalized group name (case-sensitive but trimmed)
                          const groupAccounts = accounts.filter(acc => {
                            if (!acc.group) return false;
                            const accGroup = String(acc.group).trim();
                            return accGroup === groupName;
                          });
                          const selectableGroupAccounts = groupAccounts.filter(acc => {
                            if (acc.hasValidSession !== true || acc.inUse === true) return false;
                            // Apply filter if enabled and task type is update_bio
                            if (filterNotUpdated && formData.taskType === 'update_bio' && acc.bioUpdated) return false;
                            // For dob_phone_check: exclude accounts without passwords or that already have DoB+Phone
                            if (formData.taskType === 'dob_phone_check' && acc.hasDoBAndPhone) return false;
                            if (formData.taskType === 'dob_phone_check' && !acc.hasPassword) return false;
                            // For privacy_check: exclude accounts already unchecked
                            if (formData.taskType === 'privacy_check' && acc.isUnprotected) return false;
                            return true;
                          });
                        const groupAccountIds = selectableGroupAccounts.map(acc => acc._id);
                        const allGroupSelected = selectableGroupAccounts.length > 0 && 
                          groupAccountIds.every(id => formData.accountIds.includes(id));
                        const isExpanded = expandedGroups.has(groupName);
                        
                        return (
                          <div key={groupName} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center flex-1 gap-2">
                                <button
                                  type="button"
                                  onClick={() => onToggleGroup(groupName)}
                                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                  {isExpanded ? (
                                    <ChevronDownIcon className="h-4 w-4" />
                                  ) : (
                                    <ChevronDownIcon className="h-4 w-4 transform -rotate-90" />
                                  )}
                                </button>
                                <input
                                  type="checkbox"
                                  checked={allGroupSelected}
                                  onChange={() => onSelectGroup(groupName)}
                                  disabled={selectableGroupAccounts.length === 0}
                                  className={`rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                                    selectableGroupAccounts.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                                  }`}
                                />
                                <span className={`text-sm font-medium ${
                                  selectableGroupAccounts.length === 0
                                    ? 'text-gray-400 dark:text-gray-500'
                                    : 'text-gray-700 dark:text-gray-300'
                                }`}>
                                  {groupName} ({selectableGroupAccounts.length}/{groupAccounts.length})
                                </span>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="ml-6 space-y-1 mt-1">
                                {groupAccounts
                                  .filter(acc => {
                                    // Only filter out bio updated accounts if filter is enabled
                                    if (filterNotUpdated && formData.taskType === 'update_bio' && acc.bioUpdated) return false;
                                    // Show all accounts, don't filter by hasDoBAndPhone or hasPassword - we'll disable them instead
                                    return true;
                                  })
                                  .map((account) => {
                                  const hasValidSession = account.hasValidSession === true;
                                  const inUse = account.inUse === true;
                                  // For dob_phone_check: also disable if no password or already has DoB+Phone
                                  const noPassword = formData.taskType === 'dob_phone_check' && !account.hasPassword;
                                  const hasDoBAndPhone = formData.taskType === 'dob_phone_check' && account.hasDoBAndPhone;
                                  // For privacy_check: disable if already unchecked
                                  const isUnprotected = formData.taskType === 'privacy_check' && account.isUnprotected;
                                  const isDisabled = !hasValidSession || inUse || noPassword || hasDoBAndPhone || isUnprotected;
                                  const isFilteredOut = filterNotUpdated && formData.taskType === 'update_bio' && account.bioUpdated;
                                  if (isFilteredOut) return null;
                                  
                                  // Determine status message
                                  let statusMessage = null;
                                  let statusColor = 'text-green-600 dark:text-green-400';
                                  if (!hasValidSession) {
                                    statusMessage = 'No Session';
                                    statusColor = 'text-red-600 dark:text-red-400';
                                  } else if (inUse) {
                                    statusMessage = 'In Use';
                                    statusColor = 'text-orange-600 dark:text-orange-400';
                                  } else if (noPassword) {
                                    statusMessage = 'No Pass';
                                    statusColor = 'text-red-600 dark:text-red-400';
                                  } else if (hasDoBAndPhone) {
                                    statusMessage = 'Has DoB+Phone';
                                    statusColor = 'text-yellow-600 dark:text-yellow-400';
                                  } else if (isUnprotected) {
                                    statusMessage = 'Unprotected';
                                    statusColor = 'text-yellow-600 dark:text-yellow-400';
                                  } else {
                                    statusMessage = 'Ready';
                                    statusColor = 'text-green-600 dark:text-green-400';
                                  }
                                  
                                  return (
                                    <label key={account._id} className="flex items-center justify-between">
                                      <div className="flex items-center flex-1">
                                        <input
                                          type="checkbox"
                                          checked={formData.accountIds.includes(account._id)}
                                          disabled={isDisabled}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setFormData({
                                                ...formData,
                                                accountIds: [...formData.accountIds, account._id]
                                              });
                                            } else {
                                              setFormData({
                                                ...formData,
                                                accountIds: formData.accountIds.filter(id => id !== account._id)
                                              });
                                            }
                                          }}
                                          className={`rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                                            isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                          }`}
                                        />
                                        <span className={`ml-2 text-sm ${
                                          isDisabled 
                                            ? 'text-gray-400 dark:text-gray-500' 
                                            : 'text-gray-700 dark:text-gray-300'
                                        }`}>
                                          @{account.username}
                                        </span>
                                        {formData.taskType === 'update_bio' && account.bioUpdated && (
                                          <span className="ml-2 text-xs text-green-600 dark:text-green-400" title={`Updated: ${new Date(account.lastBioUpdate).toLocaleString()}`}>
                                            ✓ Updated
                                          </span>
                                        )}
                                      </div>
                                      <span className={`ml-2 text-xs ${statusColor}`} title={
                                        !hasValidSession ? "Run Login Test for this account" :
                                        inUse ? "Account is currently being used" :
                                        noPassword ? "Account does not have a password (required for DoB check)" :
                                        hasDoBAndPhone ? "Account already has DoB and Phone" :
                                        isUnprotected ? "Account already unchecked (Privacy check)" :
                                        "Account is ready to use"
                                      }>
                                        {statusMessage === 'Ready' ? '✓ ' : '⚠️ '}{statusMessage}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      </div>
                    );
                  })()}
                  
                  {/* Ungrouped Accounts */}
                  {(() => {
                    const ungroupedAccounts = accounts.filter(acc => !acc.group || acc.group.trim() === '');
                    if (ungroupedAccounts.length === 0) return null;
                    
                    return (
                      <div className="space-y-1">
                        {groups.length > 0 && (
                          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                            Ungrouped
                          </div>
                        )}
                        {ungroupedAccounts
                          .filter(acc => {
                            // Only filter out bio updated accounts if filter is enabled
                            if (filterNotUpdated && formData.taskType === 'update_bio' && acc.bioUpdated) return false;
                            // Show all accounts, don't filter by hasDoBAndPhone or hasPassword - we'll disable them instead
                            return true;
                          })
                          .map((account) => {
                          const hasValidSession = account.hasValidSession === true;
                          const inUse = account.inUse === true;
                          // For dob_phone_check: also disable if no password or already has DoB+Phone
                          const noPassword = formData.taskType === 'dob_phone_check' && !account.hasPassword;
                          const hasDoBAndPhone = formData.taskType === 'dob_phone_check' && account.hasDoBAndPhone;
                          // For privacy_check: disable if already unchecked
                          const isUnprotected = formData.taskType === 'privacy_check' && account.isUnprotected;
                          const isDisabled = !hasValidSession || inUse || noPassword || hasDoBAndPhone || isUnprotected;
                          const isFilteredOut = filterNotUpdated && formData.taskType === 'update_bio' && account.bioUpdated;
                          if (isFilteredOut) return null;
                          
                          // Determine status message
                          let statusMessage = null;
                          let statusColor = 'text-green-600 dark:text-green-400';
                          if (!hasValidSession) {
                            statusMessage = 'No Session';
                            statusColor = 'text-red-600 dark:text-red-400';
                          } else if (inUse) {
                            statusMessage = 'In Use';
                            statusColor = 'text-orange-600 dark:text-orange-400';
                          } else if (noPassword) {
                            statusMessage = 'No Pass';
                            statusColor = 'text-red-600 dark:text-red-400';
                          } else if (hasDoBAndPhone) {
                            statusMessage = 'Has DoB+Phone';
                            statusColor = 'text-yellow-600 dark:text-yellow-400';
                          } else if (isUnprotected) {
                            statusMessage = 'Unprotected';
                            statusColor = 'text-yellow-600 dark:text-yellow-400';
                          } else {
                            statusMessage = 'Ready';
                            statusColor = 'text-green-600 dark:text-green-400';
                          }
                          
                          return (
                            <label key={account._id} className="flex items-center justify-between">
                              <div className="flex items-center flex-1">
                                <input
                                  type="checkbox"
                                  checked={formData.accountIds.includes(account._id)}
                                  disabled={isDisabled}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        accountIds: [...formData.accountIds, account._id]
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        accountIds: formData.accountIds.filter(id => id !== account._id)
                                      });
                                    }
                                  }}
                                  className={`rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                                    isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                  }`}
                                />
                                <span className={`ml-2 text-sm ${
                                  isDisabled 
                                    ? 'text-gray-400 dark:text-gray-500' 
                                    : 'text-gray-700 dark:text-gray-300'
                                }`}>
                                  @{account.username}
                                </span>
                                {formData.taskType === 'update_bio' && account.bioUpdated && (
                                  <span className="ml-2 text-xs text-green-600 dark:text-green-400" title={`Updated: ${new Date(account.lastBioUpdate).toLocaleString()}`}>
                                    ✓ Updated
                                  </span>
                                )}
                              </div>
                              <span className={`ml-2 text-xs ${statusColor}`} title={
                                !hasValidSession ? "Run Login Test for this account" :
                                inUse ? "Account is currently being used" :
                                noPassword ? "Account does not have a password (required for DoB check)" :
                                hasDoBAndPhone ? "Account already has DoB and Phone" :
                                isUnprotected ? "Account already unchecked (Privacy check)" :
                                "Account is ready to use"
                              }>
                                {statusMessage === 'Ready' ? '✓ ' : '⚠️ '}{statusMessage}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          </div>

          {/* Delay Settings */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Delay Settings</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Min Delay (seconds)
                </label>
                <input
                  type="number"
                  value={formData.settings.delayMin}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, delayMin: parseInt(e.target.value) || 2 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Max Delay (seconds)
                </label>
                <input
                  type="number"
                  value={formData.settings.delayMax}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, delayMax: parseInt(e.target.value) || 5 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500"
                  min="1"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Restart from Start Option (only when editing) */}
        {isEditing && setRestartFromStart && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={restartFromStart}
                onChange={(e) => setRestartFromStart(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Restart from the start with current settings
              </span>
            </label>
            <p className="mt-1 ml-6 text-xs text-gray-500 dark:text-gray-400">
              This will reset all progress and restart the task from the beginning. All accounts will be processed again.
            </p>
          </div>
        )}

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MassTasks;

